import React, {useEffect, useRef, useState} from "react";
import {SimmsRadio} from "../../../components/input/SimmsRadio";
import Selector from "../../../components/selector/Selector";
import {onCreateClickListener} from "../../../components/commonmethod/simmsDrawerMethod";
import {CodeSpecNum} from "../../../constant/CodeSpecNum";
import SimmsInput from "../../../components/input/SimmsInput";
import {inputOnChange} from "../../../components/commonmethod/simmsInputMethod";
import {Button} from "../../../shadCn/components/ui/button";
import SimmsInputSearch from "../../../components/input/SimmsInputSearch";
import {InspectionReportCodeChildren} from "./InspectionReportCode/subPages/InspectionReportCodeChildren";
import {inspectionReportCodeType} from "./InspectionReportCode/inspectionReportCodeConstants";
import {codeTypeNameRenderer} from "./InspectionReportCode/ChildrenCountNameRenderer";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import Th_title from "../../../components/Th_title";
import ReportCodeFormTable from "./ReportCodeFormTable";

function EditSection({title, children, required = false, headActions, variant}) {
    return (
        <section className={`user-account-edit-form__section${variant ? ` user-account-edit-form__section--${variant}` : ""}`}>
            <h3 className="user-account-edit-form__section-title report-code-edit-section__title">
                <span className="report-code-edit-section__title-text">
                    {title}
                    {required && <span className="c-red ml-1">*</span>}
                </span>
                {headActions}
            </h3>
            <div className="user-account-edit-form__section-body">{children}</div>
        </section>
    );
}

function InspectionReportCodeEditorPanel({
    item,
    setItem,
    children,
    setChildren,
    readOnly = false,
    onSave,
    onDelete,
    hideFormActions = false,
}) {
    const isFirstRender = useRef(true);
    const [activeGroupIndex, setActiveGroupIndex] = useState(0);

    useEffect(() => {
        if (children.length === 0) {
            setActiveGroupIndex(0);
            return;
        }
        if (activeGroupIndex >= children.length) {
            setActiveGroupIndex(children.length - 1);
        }
    }, [children.length, activeGroupIndex]);

    useEffect(() => {
        if (!item?.reportCode || item._isNew) {
            return;
        }
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        setItem((prev) => {
            const next = {...prev};
            delete next.reportCodeKey;
            delete next.reportCodeText;
            return next;
        });
    }, [item?.reportCode, item?._isNew, setItem]);

    useEffect(() => {
        isFirstRender.current = true;
    }, [item?.id, item?._isNew]);

    if (!item) {
        return (
            <div className="code-editor-empty">
                <div className="code-editor-empty__icon">⌘</div>
                <p className="code-editor-empty__title">코드를 선택하세요</p>
                <p className="code-editor-empty__desc">왼쪽 목록에서 코드를 선택하거나 상단 등록 버튼으로 추가하세요.</p>
            </div>
        );
    }

    const canEdit = !readOnly && (item._isNew || item.auth?.update !== false);
    const childrenStatus = readOnly ? "VIEW" : item._isNew ? "CREATE" : "UPDATE";
    const isCreateLayout = Boolean(item._isNew && hideFormActions);

    const addGroup = () => {
        const baseName = "새 그룹";
        let groupName = `${baseName} ${children.length + 1}`;
        let suffix = children.length + 2;
        while (children.some((group) => group.groupName === groupName)) {
            groupName = `${baseName} ${suffix}`;
            suffix += 1;
        }

        const nextIndex = children.length;
        setChildren([
            ...children,
            {
                groupName,
                child: [{name: "", type: 0, etc: "", groupName}],
            },
        ]);
        setActiveGroupIndex(nextIndex);
    };

    const renameGroup = (index, name) => {
        setChildren((prev) =>
            prev.map((group, groupIdx) =>
                groupIdx === index
                    ? {
                        ...group,
                        groupName: name,
                        child: group.child.map((child) => ({...child, groupName: name})),
                    }
                    : group,
            ),
        );
    };

    const removeGroup = (index) => {
        if (!window.confirm("선택한 그룹을 삭제하시겠습니까?")) {
            return;
        }
        setChildren((prev) => prev.filter((_, groupIdx) => groupIdx !== index));
        setActiveGroupIndex((prev) => {
            if (index < prev) {
                return prev - 1;
            }
            if (index === prev) {
                return Math.max(0, prev - 1);
            }
            return prev;
        });
    };

    const listDeleteBtnStyle = {
        flexShrink: 0,
        width: 20,
        height: 20,
        marginRight: 4,
        padding: 0,
        fontSize: 14,
        lineHeight: 1,
        color: "#cbd5e1",
        background: "transparent",
        border: "none",
        borderRadius: 3,
        cursor: "pointer",
    };

    const ROW_H = 38;
    const CELL_LINE = "#e5e7eb";
    const INPUT_LINE = "#eeeeee";

    const detailGroupNameStyle = {
        width: "100%",
        height: ROW_H,
        padding: "0 12px",
        fontSize: 13,
        fontWeight: 400,
        color: "#191f28",
        background: "#fff",
        border: `1px solid ${INPUT_LINE}`,
        borderRadius: 0,
        boxSizing: "border-box",
    };

    const reportCodeField = canEdit ? (
        <SimmsRadio
            options={inspectionReportCodeType}
            onChange={(v) => setItem((prev) => ({...prev, reportCode: v}))}
            data={item.reportCode}
        />
    ) : (
        <ReadOnlyInputBasic
            target="reportCode"
            value={codeTypeNameRenderer({data: item.reportCode})}
        />
    );

    const codeTypeField = canEdit ? (
        item.reportCode === "code" ? (
            <Selector
                onChange={(v) => onCreateClickListener(setItem, "reportCodeKey", item.group, v)}
                data={item.reportCodeKey}
                entity="code"
                parentKey={CodeSpecNum.AssetGroupCode}
                recursive
            />
        ) : (
            <Selector
                onChange={(v) => onCreateClickListener(setItem, "reportCodeKey", item.company, v)}
                data={item.reportCodeKey}
                entity="company"
                uniqueKey="company"
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
        )
    ) : (
        <ReadOnlyInputBasic target="reportCodeText" value={item.reportCodeText} />
    );

    const nameField = canEdit ? (
        <input
            className="basic-input input-style"
            value={item.name ?? ""}
            onChange={(e) => setItem((prev) => ({...prev, name: e.target.value}))}
        />
    ) : (
        <ReadOnlyInputBasic target="name" value={item.name} />
    );

    const etcField = canEdit ? (
        <input
            className="basic-input input-style"
            value={item.etc ?? ""}
            onChange={(e) => setItem((prev) => ({...prev, etc: e.target.value}))}
        />
    ) : (
        <ReadOnlyInputBasic target="etc" value={item.etc} />
    );

    const childrenBlock = (
        <InspectionReportCodeChildren
            children={children}
            setChildren={setChildren}
            status={childrenStatus}
            variant={isCreateLayout ? "create" : "default"}
            activeGroupIndex={activeGroupIndex}
        />
    );

    if (isCreateLayout) {
        return (
            <div className="code-editor code-editor--wide report-code-editor report-code-editor--create">
                <div className="report-code-editor__create-scroll user-account-edit-form user-account-edit-form--embedded">
                    <EditSection title="기본정보">
                        <ReportCodeFormTable>
                            <tbody>
                                <tr>
                                    <Th_title title="코드 구분" colspan={1} className="fieldset-td" required auth>
                                        {reportCodeField}
                                    </Th_title>
                                    <Th_title title="코드 타입" colspan={1} className="fieldset-td" required auth>
                                        {codeTypeField}
                                    </Th_title>
                                </tr>
                                <tr>
                                    <Th_title title="점검보고서코드 명" colspan={3} className="fieldset-td" required auth>
                                        {nameField}
                                    </Th_title>
                                </tr>
                                <tr>
                                    <Th_title title="비고" colspan={3} className="fieldset-td" auth>
                                        {etcField}
                                    </Th_title>
                                </tr>
                            </tbody>
                        </ReportCodeFormTable>
                    </EditSection>

                    <EditSection title="점검보고서 항목" required variant="split">
                        <div className="rc-split">
                            <aside className="rc-split__list" aria-label="점검 그룹">
                                <button type="button" className="rc-split__list-add" onClick={addGroup}>
                                    + 새 그룹
                                </button>
                                {children.length > 0 ? (
                                    <ul className="rc-split__list-items">
                                        {children.map((group, index) => (
                                            <li
                                                key={`${group.groupName}-${index}`}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    background: activeGroupIndex === index ? "#fff" : "transparent",
                                                    boxShadow: activeGroupIndex === index
                                                        ? "inset 3px 0 0 var(--tehemeDark, #1e293b)"
                                                        : "none",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className={`rc-split__list-btn${activeGroupIndex === index ? " is-active" : ""}`}
                                                    onClick={() => setActiveGroupIndex(index)}
                                                    style={{flex: 1, minWidth: 0}}
                                                >
                                                    <span className="rc-split__list-no">{index + 1}</span>
                                                    <span className="rc-split__list-name">
                                                        {group.groupName?.trim() || "이름 없음"}
                                                        <span className="rc-split__list-meta-inline">
                                                            · {group.child?.length ?? 0}
                                                        </span>
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-label={`${group.groupName} 그룹 삭제`}
                                                    style={listDeleteBtnStyle}
                                                    onClick={() => removeGroup(index)}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.color = "#e42939";
                                                        e.currentTarget.style.background = "#fff0f0";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.color = "#cbd5e1";
                                                        e.currentTarget.style.background = "transparent";
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="rc-split__list-empty">등록된 그룹 없음</p>
                                )}
                            </aside>

                            <div className="rc-split__detail">
                                {children.length > 0 ? (
                                    <>
                                        <header
                                            style={{
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "stretch",
                                                height: ROW_H,
                                                minHeight: ROW_H,
                                                borderBottom: "1px solid #e5e7eb",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    flexShrink: 0,
                                                    width: 72,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "0 12px",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: "#6b7280",
                                                    background: "#f9fafb",
                                                    borderRight: "1px solid #e5e7eb",
                                                    boxSizing: "border-box",
                                                }}
                                            >
                                                그룹명
                                            </span>
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: "flex",
                                                    alignItems: "stretch",
                                                    border: `1px solid ${CELL_LINE}`,
                                                    borderTop: 0,
                                                    borderBottom: 0,
                                                    borderLeft: 0,
                                                    boxSizing: "border-box",
                                                }}
                                            >
                                                <SimmsInput
                                                    onChange={(v) => renameGroup(activeGroupIndex, v)}
                                                    data={children[activeGroupIndex]?.groupName ?? ""}
                                                    placeHolder="그룹 이름을 입력하세요"
                                                    style={detailGroupNameStyle}
                                                />
                                            </div>
                                        </header>
                                        <div className="rc-split__detail-body">{childrenBlock}</div>
                                    </>
                                ) : (
                                    <div className="rc-split__detail-empty">
                                        <p>점검 항목을 묶을 그룹을 먼저 만드세요.</p>
                                        <button type="button" onClick={addGroup}>
                                            + 새 그룹
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </EditSection>
                </div>
            </div>
        );
    }

    return (
        <div className="code-editor code-editor--wide report-code-editor">
            <span className="code-editor__meta">점검보고서 설정</span>

            {item.useCount > 0 && (
                <p className="report-code-editor__notice">
                    사용 건수 {item.useCount}건 — 사용 중인 코드는 수정·삭제할 수 없습니다.
                </p>
            )}

            <div className="report-code-editor__section">
                <div className="code-editor__field code-editor__field--inline">
                    <label className="code-editor__inline-label">
                        코드 구분<span className="required">*</span>
                    </label>
                    {reportCodeField}
                </div>

                <div className="code-editor__field code-editor__field--inline">
                    <label className="code-editor__inline-label">
                        코드 타입<span className="required">*</span>
                    </label>
                    {codeTypeField}
                </div>

                <div className="code-editor__field">
                    <label>
                        점검보고서코드 명<span className="required">*</span>
                    </label>
                    {nameField}
                </div>

                <div className="code-editor__field">
                    <label>비고</label>
                    {etcField}
                </div>

                {canEdit && (
                    <div className="code-editor__field code-editor__field--inline">
                        <label className="code-editor__inline-label">점검보고서 그룹 추가</label>
                        <SimmsInput
                            onChange={(v) => inputOnChange(setItem, "groupName", v, false)}
                            data={item.groupName}
                            style={{width: "240px", marginRight: "8px"}}
                        />
                        <Button type="button" variant="outline" onClick={addGroup} className="h-[33px]">
                            추가
                        </Button>
                    </div>
                )}
            </div>

            <div className="report-code-editor__children">
                {childrenBlock}
            </div>

            {!hideFormActions && (canEdit || (!item._isNew && item.useCount === 0)) && (
                <div className="code-editor__actions">
                    {!item._isNew && item.useCount === 0 && onDelete && (
                        <button type="button" className="code-manage-delete-btn" onClick={onDelete}>
                            삭제
                        </button>
                    )}
                    {canEdit && onSave && (
                        <button type="button" className="save-btn" onClick={onSave}>
                            {item._isNew ? "등록" : "저장"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default InspectionReportCodeEditorPanel;
